/**
 * Client-side AI Tool Executor.
 *
 * Executes tool calls locally using @totodo/operations against the
 * browser's Dexie instance. Changes sync to all devices via Dexie Cloud.
 *
 * This replaces the server-side execution that required IMPERSONATE tokens.
 */

import {
  addTask,
  completeTask,
  uncompleteTask,
  cancelTask,
  scheduleTask,
  clearSchedule,
  moveTask,
  moveMultiple,
  reorderTask,
  addProject,
  addList,
  addArea,
  addSection,
  renameContainer,
  updateContainerIcon,
  searchTasks,
  queryTodayTasks,
  queryEveningTasks,
  queryUpcomingTasks,
  querySomedayTasks,
  queryAnydayTasks,
  queryInbox,
  queryLogbook,
  queryTrash,
  listContainers,
  listTasks,
  deleteTask,
  restoreTask,
  duplicateTask,
  permanentlyDeleteTask,
  emptyTrash,
  convertTaskToProject,
  convertTaskToSection,
  updateTaskTitle,
  updateTaskNote,
  updateTaskDueDate,
  updateTaskTags,
  addChecklistItem,
  toggleChecklistItem,
  deleteChecklistItem,
  updateChecklistTitle,
  promoteChecklistToTask,
  getChecklistItems,
  addReminder,
  getTaskReminder,
  updateReminder,
  deleteReminder,
  bulkComplete,
  bulkDelete,
  bulkSchedule,
  bulkUncomplete,
  bulkCancel,
  bulkRestore,
  bulkUpdateDueDate,
  executePlan,
  listPacks,
  getTaskDetail,
  listAllTags,
  setTaskDoc,
  hasDocContext,
  nextIndex,
  createRecurrence,
  BUILT_IN_PACKS,
  ObjectType,
  ObjectStatus,
  StartAtType,
  StartAtSubType,
  parseDbDate,
} from "@totodo/operations"
import type { DbObject, OpResult, DbPack } from "@totodo/operations"
import { db } from "@/db/schema"
import { useUndoStore } from "@/shared/undo/undoStore"
import {
  readArea,
  readEntry,
  saveEntry,
  updateEntry as updateMemoryEntry,
  listAreas,
  type MemoryArea,
} from "@/services/ai-memory"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ToolCallArgs {
  [key: string]: unknown
}

export interface ToolExecResult {
  success: boolean
  data?: unknown
  error?: string
  /** Full objects for rich UI rendering (not sent to AI) */
  _renderData?: unknown
}

// ---------------------------------------------------------------------------
// Executor
// ---------------------------------------------------------------------------

export async function executeToolCall(
  name: string,
  args: ToolCallArgs,
): Promise<ToolExecResult> {
  try {
    switch (name) {
      case "createTask": {
        // Normalize parentPath: must start with "root/" or be omitted
        let parentPath = args.parentPath as string | undefined
        if (parentPath) {
          parentPath = parentPath.replace(/\/$/, "") // strip trailing slash
          if (!parentPath.startsWith("root/")) {
            // AI sent bare container ID — prefix with root/
            parentPath = `root/${parentPath}`
          }
        }

        const noteContent = args.note as string | undefined
        const noteFormat = (args.noteFormat as string | undefined) ?? "markdown"

        const opts: Parameters<typeof addTask>[0] = {
          title: args.title as string,
          // Use docContent for proper Yjs doc creation when note is provided
          ...(noteContent ? { docContent: noteContent, docFormat: noteFormat as "text" | "html" | "markdown" } : {}),
          note: noteContent ?? "",
          parentPath,
          insertPosition: (args.insertPosition as "top" | "bottom") ?? "top",
          tags: (args.tags as string[] | undefined)?.map((t) => t.replace(/^#/, "")),
          dueAt: args.dueAt as string | undefined,
        }

        // Handle schedule shortcut
        if (args.schedule) {
          switch (args.schedule) {
            case "today":
              opts.startAtType = StartAtType.Default
              opts.startAt = new Date().toISOString().slice(0, 10)
              break
            case "evening":
              opts.startAtType = StartAtType.Default
              opts.startAtSubType = StartAtSubType.ThisEvening
              opts.startAt = new Date().toISOString().slice(0, 10)
              break
            case "tomorrow": {
              opts.startAtType = StartAtType.Default
              const tomorrow = new Date()
              tomorrow.setDate(tomorrow.getDate() + 1)
              opts.startAt = tomorrow.toISOString().slice(0, 10)
              break
            }
            case "someday":
              opts.startAtType = StartAtType.Someday
              break
            case "anyday":
              break
          }
        } else if (args.startAt) {
          // Specific date (ISO YYYY-MM-DD) — schedule for that date
          opts.startAtType = StartAtType.Default
          opts.startAt = args.startAt as string
        }

        const result = await addTask(opts)
        if (!result.ok) return { success: false, error: result.error }

        // Register undo so AI can undo this create
        const createdId = result.data.id
        useUndoStore.getState().push({
          id: crypto.randomUUID(),
          timestamp: Date.now(),
          description: `Create task: ${opts.title}`,
          type: "create",
          undo: async () => { await deleteTask(createdId) },
          redo: async () => { await addTask(opts) },
        })

        // Handle repeat/recurrence if specified
        if (args.repeat) {
          const repeatMap: Record<string, string> = {
            daily: "FREQ=DAILY",
            weekdays: "FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR",
            weekly: "FREQ=WEEKLY",
            biweekly: "FREQ=WEEKLY;INTERVAL=2",
            monthly: "FREQ=MONTHLY",
            yearly: "FREQ=YEARLY",
          }
          const rrule = repeatMap[args.repeat as string]
          if (rrule) {
            const startDate = opts.startAt || new Date().toISOString().slice(0, 10)
            try {
              await createRecurrence({
                templateId: createdId,
                mode: "FIXED",
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
                startAt: startDate,
                rrule,
              })
            } catch (e) {
              console.warn("[AI] Failed to create recurrence:", e)
              // Task was still created successfully, just no recurrence
            }
          }
        }

        // Include resolved date with day-of-week so AI can narrate accurately
        let scheduledInfo: { date: string; dayName: string } | undefined
        if (opts.startAt) {
          const svDays = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"]
          const d = new Date(opts.startAt + "T12:00:00")
          scheduledInfo = { date: opts.startAt, dayName: svDays[d.getDay()]! }
        }

        return {
          success: true,
          data: { id: createdId, title: opts.title, created: true, ...(scheduledInfo && { scheduledDate: scheduledInfo.date, scheduledDayName: scheduledInfo.dayName }) },
        }
      }

      case "completeTask": {
        const result = await completeTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "uncompleteTask": {
        const result = await uncompleteTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "deleteTask": {
        const result = await deleteTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "updateTask": {
        const taskId = args.taskId as string
        const results: OpResult[] = []

        if (args.title) {
          results.push(await updateTaskTitle(taskId, args.title as string))
        }
        if (args.note !== undefined) {
          const noteStr = args.note as string
          const noteFormat = (args.noteFormat as string | undefined) ?? "markdown"
          // Use setTaskDoc for proper Yjs document creation (shows in TipTap editor)
          if (noteStr && hasDocContext()) {
            results.push(await setTaskDoc(taskId, noteStr, noteFormat as "text" | "html" | "markdown"))
          } else {
            results.push(await updateTaskNote(taskId, noteStr))
          }
        }
        if (args.dueAt !== undefined) {
          results.push(
            await updateTaskDueDate(
              taskId,
              (args.dueAt as string | null) ?? undefined,
            ),
          )
        }
        if (args.tags) {
          results.push(await updateTaskTags(taskId, (args.tags as string[]).map((t) => t.replace(/^#/, ""))))
        }

        const failed = results.find((r) => !r.ok)
        if (failed && !failed.ok) {
          return { success: false, error: failed.error }
        }
        return { success: true, data: { taskId, updated: true } }
      }

      case "scheduleTask": {
        const taskId = args.taskId as string
        const when = args.when as string | undefined
        const date = args.date as string | undefined
        const svDays = ["söndag", "måndag", "tisdag", "onsdag", "torsdag", "fredag", "lördag"]

        if (date) {
          const result = await scheduleTask(taskId, date, StartAtType.Default)
          if (!result.ok) return { success: false, error: result.error }
          const d = new Date(date + "T12:00:00")
          return { success: true, data: { scheduled: true, date, dayName: svDays[d.getDay()]! } }
        }

        switch (when) {
          case "today": {
            const today = new Date().toISOString().slice(0, 10)
            const result = await scheduleTask(taskId, today, StartAtType.Default)
            if (!result.ok) return { success: false, error: result.error }
            return { success: true, data: { scheduled: true, date: today, dayName: svDays[new Date().getDay()]! } }
          }
          case "evening": {
            const today = new Date().toISOString().slice(0, 10)
            const result = await scheduleTask(
              taskId,
              today,
              StartAtType.Default,
              StartAtSubType.ThisEvening,
            )
            return wrapOpResult(result)
          }
          case "tomorrow": {
            const tomorrow = new Date()
            tomorrow.setDate(tomorrow.getDate() + 1)
            const tomorrowStr = tomorrow.toISOString().slice(0, 10)
            const result = await scheduleTask(taskId, tomorrowStr, StartAtType.Default)
            if (!result.ok) return { success: false, error: result.error }
            return { success: true, data: { scheduled: true, date: tomorrowStr, dayName: svDays[tomorrow.getDay()]! } }
          }
          case "someday": {
            const result = await scheduleTask(
              taskId,
              undefined,
              StartAtType.Someday,
            )
            return wrapOpResult(result)
          }
          case "anyday": {
            const result = await scheduleTask(
              taskId,
              undefined,
              StartAtType.Default,
            )
            return wrapOpResult(result)
          }
          default:
            return { success: false, error: `Unknown schedule option: ${when}` }
        }
      }

      case "moveTask": {
        // Support query-based lookup: taskQuery → search for task, containerQuery → search for container
        let taskId = args.taskId as string | undefined
        let dest = args.destinationId as string | undefined

        // Resolve task by query if taskId not given or not UUID-like
        if (!taskId || (args.taskQuery && !taskId.match(/^[0-9a-f-]{36}$/i))) {
          const query = (args.taskQuery as string) || (taskId as string)
          if (!query) return { success: false, error: "No task specified" }
          const searchResult = await searchTasks(query, 5, "active")
          if (!searchResult.ok || searchResult.data.length === 0) {
            return { success: false, error: `Task not found: "${query}"` }
          }
          taskId = searchResult.data[0]!.id
        }

        // Resolve container by query if destinationId not given or not UUID/#-prefixed
        if (!dest || (args.containerQuery && !dest.match(/^[0-9a-f-]{36}$/i) && !dest.startsWith("#"))) {
          const query = (args.containerQuery as string) || (dest as string)
          if (!query) return { success: false, error: "No destination specified" }
          if (query === "inbox" || query === "#inbox") {
            dest = "#inbox"
          } else if (query === "anyday" || query === "#anyday") {
            dest = "#anyday"
          } else {
            const containers = await listContainers()
            if (!containers.ok) return { success: false, error: "Failed to list containers" }
            const normalizedQuery = query.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
            const match = containers.data.find((c) =>
              c.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(normalizedQuery),
            )
            if (!match) return { success: false, error: `Container not found: "${query}"` }
            dest = match.id
          }
        }

        // Normalize: moveTask expects parentPath format "root/<containerId>"
        if (dest && !dest.startsWith("root/")) {
          dest = `root/${dest}`
        }
        if (!dest) return { success: false, error: "No destination resolved" }
        const result = await moveTask(
          taskId!,
          dest,
        )
        if (!result.ok) return { success: false, error: result.error }
        // Return container title so AI can confirm correctly
        const destId = dest.replace("root/", "")
        let containerTitle = destId
        if (!destId.startsWith("#")) {
          const container = await db.totodo.get(destId)
          if (container?.title) containerTitle = container.title
        } else {
          containerTitle = destId === "#inbox" ? "Inbox" : destId === "#anyday" ? "Anyday" : destId
        }
        return { success: true, data: { moved: true, containerTitle } }
      }

      case "reorderTaskInList": {
        const taskId = args.taskId as string
        const direction = args.direction as "up" | "down" | undefined
        const targetPosition = args.position as number | undefined

        // Get the task to find its parentPath
        const task = await db.totodo.get(taskId)
        if (!task) return { success: false, error: "Task not found" }

        // Get all task siblings (exclude sections, deleted, completed, canceled, and self)
        const siblings = await db.totodo
          .where("parentPath")
          .equals(task.parentPath)
          .sortBy("index")
        const activeSiblings = siblings.filter(
          (s) =>
            s.id !== taskId &&
            s.type === ObjectType.Task &&
            s.isDeleted !== 1 &&
            s.status !== ObjectStatus.Completed &&
            s.status !== ObjectStatus.Canceled,
        )

        let newIndex: number

        if (targetPosition !== undefined) {
          // Move to specific position (1-based) among other tasks
          const pos = Math.max(0, Math.min(targetPosition - 1, activeSiblings.length))

          if (pos === 0) {
            // Before first item
            newIndex = (activeSiblings[0]?.index ?? task.index) - 1
          } else if (pos >= activeSiblings.length) {
            // After last item
            newIndex = (activeSiblings[activeSiblings.length - 1]?.index ?? task.index) + 1
          } else {
            // Between two items
            const before = activeSiblings[pos - 1]!.index
            const after = activeSiblings[pos]!.index
            newIndex = nextIndex(before, after)
          }
        } else if (direction === "up") {
          // For direction, we need the task in the list to find its relative position
          const allWithSelf = siblings.filter(
            (s) =>
              s.type === ObjectType.Task &&
              s.isDeleted !== 1 &&
              s.status !== ObjectStatus.Completed &&
              s.status !== ObjectStatus.Canceled,
          )
          const currentIdx = allWithSelf.findIndex((s) => s.id === taskId)
          if (currentIdx <= 0) return { success: true, data: { message: "Already at the top" } }
          const above = allWithSelf[currentIdx - 1]!.index
          const aboveAbove = currentIdx >= 2 ? allWithSelf[currentIdx - 2]!.index : undefined
          newIndex = nextIndex(aboveAbove, above)
        } else if (direction === "down") {
          const allWithSelf = siblings.filter(
            (s) =>
              s.type === ObjectType.Task &&
              s.isDeleted !== 1 &&
              s.status !== ObjectStatus.Completed &&
              s.status !== ObjectStatus.Canceled,
          )
          const currentIdx = allWithSelf.findIndex((s) => s.id === taskId)
          if (currentIdx === -1 || currentIdx === allWithSelf.length - 1) return { success: true, data: { message: "Already at the bottom" } }
          const below = allWithSelf[currentIdx + 1]!.index
          const belowBelow = currentIdx < allWithSelf.length - 2 ? allWithSelf[currentIdx + 2]!.index : undefined
          newIndex = nextIndex(below, belowBelow)
        } else {
          return { success: false, error: "Specify 'direction' (up/down) or 'position' (number)" }
        }

        const result = await reorderTask(taskId, newIndex)
        return wrapOpResult(result)
      }

      case "createProject": {
        const result = await addProject(args.title as string)
        return wrapOpResult(result)
      }

      case "createList": {
        const result = await addList(args.title as string)
        return wrapOpResult(result)
      }

      case "createProjectWithTasks": {
        const projectResult = await addProject(args.projectTitle as string)
        if (!projectResult.ok) {
          return { success: false, error: projectResult.error }
        }

        const projectId = projectResult.data.id
        const tasks = args.tasks as Array<{
          title: string
          note?: string
          dueAt?: string
          tags?: string[]
        }>

        // Create tasks sequentially with "bottom" position to preserve order
        const created: string[] = []
        for (const t of tasks) {
          const result = await addTask({
            title: t.title,
            ...(t.note ? { docContent: t.note, docFormat: "markdown" as const } : {}),
            note: t.note ?? "",
            dueAt: t.dueAt,
            tags: t.tags,
            parentPath: `root/${projectId}`,
            insertPosition: "bottom",
          })
          if (result.ok) created.push(result.data.id)
        }

        return {
          success: true,
          data: { projectId, tasksCreated: created.length },
        }
      }

      case "searchTasks": {
        const result = await searchTasks(
          args.query as string || "",
          (args.limit as number) ?? 20,
          (args.status as "active" | "completed" | "completed_only" | "canceled" | "all") ?? "active",
          args.objectType as "task" | "project" | "list" | "area" | "section" | undefined,
          args.tag as string | undefined,
          args.userStatus as string | undefined,
        )
        if (!result.ok) return { success: false, error: result.error }
        // Resolve container titles for disambiguation
        const items = await Promise.all(
          result.data.map(async (task) => {
            const summary = summarizeTask(task)
            if (summary.containerKey && !summary.containerKey.startsWith("#")) {
              const container = await db.totodo.get(summary.containerKey)
              if (container) {
                return { ...summary, containerTitle: container.title }
              }
            } else if (summary.containerKey === "#inbox") {
              return { ...summary, containerTitle: "Inbox" }
            } else if (summary.containerKey === "#anyday") {
              return { ...summary, containerTitle: "Anyday" }
            }
            return summary
          }),
        )
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...item })) },
          _renderData: result.data,
        }
      }

      case "getTodayTasks": {
        const result = await queryTodayTasks()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getUpcomingTasks": {
        const result = await queryUpcomingTasks()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getInboxTasks": {
        const result = await queryInbox()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getTasksWithDeadline": {
        // Find tasks with due dates within a range (default: next 7 days)
        const daysAhead = (args.days as number) ?? 7
        const now = Date.now()
        const cutoff = now + daysAhead * 24 * 60 * 60 * 1000
        const allTasks = await db.totodo
          .filter(
            (t) =>
              t.isDeleted !== 1 &&
              t.type === ObjectType.Task &&
              t.status !== ObjectStatus.Completed &&
              t.status !== ObjectStatus.Canceled &&
              t.dueAt != null &&
              t.dueAt !== "",
          )
          .toArray()
        const upcoming = allTasks
          .filter((t) => {
            const dueMs = parseDbDate(t.dueAt)
            return dueMs !== undefined && dueMs <= cutoff
          })
          .sort((a, b) => (parseDbDate(a.dueAt) ?? 0) - (parseDbDate(b.dueAt) ?? 0))
          .slice(0, 50)
        return {
          success: true,
          data: { count: upcoming.length, items: upcoming.map((t, i) => ({ "#": i + 1, ...summarizeTask(t) })) },
          _renderData: upcoming,
        }
      }

      case "searchContainers": {
        const rawQuery = (args.query as string).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
        // Strip Swedish definite suffixes: "inköpslistan"→"inköps"→"inköp", "dexieprojektet"→"dexie"
        const query = rawQuery
          .replace(/projektet$/, "")
          .replace(/projekt$/, "")
          .replace(/listan$/, "")
          .replace(/(et|en|n)$/, "") // definite article suffixes
          .replace(/-$/, "") // trailing dash from suffix stripping
          .trim() || rawQuery // fallback to original if stripping empties it
        const result = await listContainers()
        if (!result.ok) return { success: false, error: result.error }
        // Try exact includes first, then fall back to stripped query
        let matches = result.data
          .filter((c) => c.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(rawQuery))
          .slice(0, 10)
        if (matches.length === 0 && query !== rawQuery) {
          matches = result.data
            .filter((c) => c.title.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes(query))
            .slice(0, 10)
        }
        return {
          success: true,
          data: matches.map((c) => ({
            id: c.id,
            title: c.title,
            type: c.type === ObjectType.Project ? "project" : c.type === ObjectType.List ? "list" : "area",
            parentPath: `root/${c.id}`,
            moveDestinationId: c.id,
          })),
          _renderData: matches,
        }
      }

      case "getContainers": {
        const typeArg = args.type as string | undefined
        const typeMap: Record<string, number> = {
          project: ObjectType.Project,
          list: ObjectType.List,
          area: ObjectType.Area,
        }
        const filterType = typeArg ? typeMap[typeArg] : undefined
        const result = await listContainers(filterType !== undefined ? { type: filterType } : undefined)
        if (!result.ok) return { success: false, error: result.error }
        let containers = result.data
        // Filter by hasDeadline if requested
        if (args.hasDeadline === true) {
          containers = containers.filter((c) => c.dueAt != null && c.dueAt !== "")
        }
        // Filter by deadline date range
        if (args.deadlineAfter || args.deadlineBefore) {
          const afterMs = args.deadlineAfter ? new Date(args.deadlineAfter as string).getTime() : undefined
          const beforeMs = args.deadlineBefore ? new Date(args.deadlineBefore as string).getTime() : undefined
          containers = containers.filter((c) => {
            if (!c.dueAt) return false
            const dueMs = parseDbDate(c.dueAt)
            if (dueMs === undefined) return false
            if (afterMs && dueMs < afterMs) return false
            if (beforeMs && dueMs > beforeMs) return false
            return true
          })
        }
        // Exclude completed/canceled unless explicitly included
        if (args.includeCompleted !== true) {
          containers = containers.filter(
            (c) => c.status !== ObjectStatus.Completed && c.status !== ObjectStatus.Canceled,
          )
        }
        // Filter shared only if requested
        if (args.shared === true) {
          const currentUserId = db.cloud?.currentUserId
          containers = containers.filter(
            (c) => c.realmId && typeof c.realmId === "string" && c.realmId !== currentUserId && c.realmId !== "rlm",
          )
        }
        const currentUserId = db.cloud?.currentUserId
        return {
          success: true,
          data: {
            count: containers.length,
            items: containers.map((c, i) => ({
              "#": i + 1,
              id: c.id,
              title: c.title,
              type: c.type === ObjectType.Project ? "project" : c.type === ObjectType.List ? "list" : "area",
              dueAt: c.dueAt ?? null,
              status: c.status === ObjectStatus.Completed ? "completed" : c.status === ObjectStatus.Canceled ? "canceled" : "active",
              shared: !!(c.realmId && typeof c.realmId === "string" && c.realmId !== currentUserId && c.realmId !== "rlm"),
              taskCount: null, // Could be computed but expensive
            })),
          },
          _renderData: containers,
        }
      }

      case "duplicateTask": {
        const result = await duplicateTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "restoreTask": {
        const result = await restoreTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "cancelTask": {
        const result = await cancelTask(args.taskId as string)
        return wrapOpResult(result)
      }

      case "clearSchedule": {
        const result = await clearSchedule(args.taskId as string)
        return wrapOpResult(result)
      }

      case "createArea": {
        const result = await addArea(args.title as string)
        return wrapOpResult(result)
      }

      case "renameContainer": {
        const result = await renameContainer(
          args.containerId as string,
          args.title as string,
        )
        return wrapOpResult(result)
      }

      case "addChecklistItem": {
        const taskId = args.taskId as string
        // Need to get task's parentPath for checklist item creation
        const task = await db.totodo.get(taskId)
        if (!task) return { success: false, error: "Task not found" }
        const result = await addChecklistItem(
          taskId,
          task.parentPath,
          args.title as string,
        )
        return wrapOpResult(result)
      }

      case "addReminder": {
        const result = await addReminder(
          args.taskId as string,
          args.fireAt as string,
        )
        return wrapOpResult(result)
      }

      case "deleteReminder": {
        const result = await deleteReminder(args.reminderId as string)
        return wrapOpResult(result)
      }

      case "getEveningTasks": {
        const result = await queryEveningTasks()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getSomedayTasks": {
        const result = await querySomedayTasks()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getAnydayTasks": {
        const result = await queryAnydayTasks()
        if (!result.ok) return { success: false, error: result.error }
        const items = result.data.items
        return {
          success: true,
          data: { count: items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getLogbook": {
        const result = await queryLogbook()
        if (!result.ok) return { success: false, error: result.error }
        const limit = (args.limit as number) ?? 30
        const items = result.data.items.slice(0, limit)
        return {
          success: true,
          data: { count: items.length, totalCount: result.data.items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "getTrash": {
        const result = await queryTrash()
        if (!result.ok) return { success: false, error: result.error }
        const limit = (args.limit as number) ?? 30
        const items = result.data.items.slice(0, limit)
        return {
          success: true,
          data: { count: items.length, totalCount: result.data.items.length, items: items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item.task) })) },
          _renderData: items.map((item) => item.task),
        }
      }

      case "listTasks": {
        const parentPath = args.parentPath as string
        const limit = (args.limit as number) ?? 50
        const result = await listTasks({ parentPath, limit })
        if (!result.ok) return { success: false, error: result.error }
        return {
          success: true,
          data: { count: result.data.items.length, items: result.data.items.map((item, i) => ({ "#": i + 1, ...summarizeTask(item) })) },
          _renderData: result.data.items,
        }
      }

      case "listTags": {
        const result = await listAllTags()
        if (!result.ok) return { success: false, error: result.error }
        return { success: true, data: { tags: result.data } }
      }

      case "bulkComplete": {
        const result = await bulkComplete(args.taskIds as string[])
        return wrapOpResult(result)
      }

      case "bulkDelete": {
        const result = await bulkDelete(args.taskIds as string[])
        return wrapOpResult(result)
      }

      case "bulkSchedule": {
        const taskIds = args.taskIds as string[]
        const when = args.when as string | undefined
        const date = args.date as string | undefined

        let startAt: string
        let typeNum = StartAtType.Default

        if (date) {
          startAt = date
        } else {
          switch (when) {
            case "today":
              startAt = new Date().toISOString().slice(0, 10)
              break
            case "tomorrow": {
              const tomorrow = new Date()
              tomorrow.setDate(tomorrow.getDate() + 1)
              startAt = tomorrow.toISOString().slice(0, 10)
              break
            }
            case "someday":
              startAt = new Date().toISOString().slice(0, 10)
              typeNum = StartAtType.Someday
              break
            default:
              startAt = new Date().toISOString().slice(0, 10)
          }
        }

        const result = await bulkSchedule(taskIds, startAt, typeNum)
        return wrapOpResult(result)
      }

      case "listPacks": {
        const category = args.category as string | undefined
        const userPacks = await listPacks(category ? { category } : undefined)
        let packs = [...BUILT_IN_PACKS, ...userPacks]
        if (category) packs = packs.filter((p) => p.category === category)
        const summary = packs.map((p: DbPack) => ({
          id: p.id,
          name: p.name,
          description: p.description,
          category: p.category,
          steps: p.steps.length,
          isBuiltIn: p.isBuiltIn,
        }))
        return { success: true, data: summary }
      }

      case "getPlanPackDetails": {
        const packId = args.packId as string
        let pack: DbPack | undefined = BUILT_IN_PACKS.find((p) => p.id === packId)
        if (!pack) {
          const entry = await db.plan.get(packId)
          if (entry?.kind === "pack") pack = entry as DbPack
        }
        if (!pack) return { success: false, error: `Pack not found: ${packId}` }
        return {
          success: true,
          data: {
            id: pack.id,
            name: pack.name,
            description: pack.description,
            questions: pack.questions?.map((q) => ({
              key: q.key,
              label: q.label,
              type: q.type,
              required: q.required ?? false,
              options: q.options,
              default: q.default,
              condition: q.condition,
            })) ?? [],
            stepCount: pack.steps.length,
            stepPreviews: pack.steps.slice(0, 5).map((s) => s.titleTemplate),
          },
        }
      }

      case "executePlan": {
        const packId = args.packId as string
        const anchorTaskId = args.anchorTaskId as string
        const anchorDate = args.anchorDate as string
        const answers = (args.answers as Record<string, unknown>) ?? {}

        // Find the pack
        let pack: DbPack | undefined = BUILT_IN_PACKS.find((p) => p.id === packId)
        if (!pack) {
          const entry = await db.plan.get(packId)
          if (entry?.kind === "pack") pack = entry as DbPack
        }
        if (!pack) return { success: false, error: `Pack not found: ${packId}` }

        const anchorMs = isNaN(Number(anchorDate))
          ? new Date(anchorDate).getTime()
          : Number(anchorDate)

        const result = await executePlan({
          anchorTaskId,
          anchorDate: anchorMs,
          anchorDateType: pack.anchorDateType,
          pack,
          answers,
        })
        if (!result.ok) return { success: false, error: result.error }
        return {
          success: true,
          data: {
            planInstanceId: result.data.planInstanceId,
            taskCount: result.data.taskIds.length,
          },
        }
      }

      // -------------------------------------------------------------------
      // Sections
      // -------------------------------------------------------------------

      case "addSection": {
        const containerId = args.containerId as string
        const parentPath = `root/${containerId}`
        const result = await addSection(args.title as string, parentPath)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Checklist operations
      // -------------------------------------------------------------------

      case "getTaskChecklist": {
        const result = await getChecklistItems(args.taskId as string)
        if (!result.ok) return { success: false, error: result.error }
        return { success: true, data: result.data }
      }

      case "toggleChecklistItem": {
        const result = await toggleChecklistItem(args.itemId as string)
        return wrapOpResult(result)
      }

      case "deleteChecklistItem": {
        const result = await deleteChecklistItem(args.itemId as string)
        return wrapOpResult(result)
      }

      case "updateChecklistItem": {
        const result = await updateChecklistTitle(
          args.itemId as string,
          args.title as string,
        )
        return wrapOpResult(result)
      }

      case "promoteToTask": {
        const itemId = args.itemId as string
        const item = await db.totodo.get(itemId)
        if (!item) return { success: false, error: "Checklist item not found" }
        // The item's parentPath is like "root/<containerId>/<taskId>"
        // The new task should go into the container: "root/<containerId>"
        const parts = item.parentPath.split("/")
        parts.pop() // Remove taskId to get the container parentPath
        const containerParentPath = parts.join("/")
        const result = await promoteChecklistToTask(itemId, containerParentPath)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Container icon
      // -------------------------------------------------------------------

      case "setContainerIcon": {
        const icon = (args.icon as string) || undefined
        const result = await updateContainerIcon(args.containerId as string, icon)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Batch move
      // -------------------------------------------------------------------

      case "moveMultipleTasks": {
        let dest = args.destinationId as string
        if (dest && !dest.startsWith("root/")) {
          dest = `root/${dest}`
        }
        const result = await moveMultiple(args.taskIds as string[], dest)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Task conversion
      // -------------------------------------------------------------------

      case "convertToProject": {
        const result = await convertTaskToProject(args.taskId as string)
        return wrapOpResult(result)
      }

      case "convertToSection": {
        const result = await convertTaskToSection(args.taskId as string)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Trash management
      // -------------------------------------------------------------------

      case "emptyTrash": {
        const result = await emptyTrash()
        return wrapOpResult(result)
      }

      case "permanentlyDelete": {
        const result = await permanentlyDeleteTask(args.taskId as string)
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Reminder management
      // -------------------------------------------------------------------

      case "getTaskReminder": {
        const reminder = await getTaskReminder(args.taskId as string)
        if (!reminder) {
          return { success: true, data: { hasReminder: false } }
        }
        return {
          success: true,
          data: {
            hasReminder: true,
            reminderId: reminder.id,
            fireAt: reminder.fireAt,
            kind: reminder.kind,
          },
        }
      }

      case "updateReminder": {
        const result = await updateReminder(args.reminderId as string, {
          fireAt: args.fireAt as string,
        })
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Additional bulk operations
      // -------------------------------------------------------------------

      case "bulkUncomplete": {
        const result = await bulkUncomplete(args.taskIds as string[])
        return wrapOpResult(result)
      }

      case "bulkCancel": {
        const result = await bulkCancel(args.taskIds as string[])
        return wrapOpResult(result)
      }

      case "bulkRestore": {
        const result = await bulkRestore(args.taskIds as string[])
        return wrapOpResult(result)
      }

      case "bulkUpdateDueDate": {
        const result = await bulkUpdateDueDate(
          args.taskIds as string[],
          (args.dueAt as string) || undefined,
        )
        return wrapOpResult(result)
      }

      // -------------------------------------------------------------------
      // Task detail
      // -------------------------------------------------------------------

      case "getTaskDetail": {
        const result = await getTaskDetail(args.taskId as string)
        if (!result.ok) return { success: false, error: result.error }
        return { success: true, data: result.data }
      }

      case "askUserChoice": {
        // This tool is a UI-only interaction — echo the options back.
        // The ChatMessageBubble will detect this and render interactive buttons.
        // The AI gets "waiting for user selection" as the result.
        return {
          success: true,
          data: {
            question: args.question as string,
            options: args.options as Array<{ label: string; value: string; description?: string }>,
            type: (args.type as string) ?? "single",
            _uiAction: "choice",
          },
        }
      }

      case "showForm": {
        // Render an interactive form inline — user fills it in and submits.
        return {
          success: true,
          data: {
            title: args.title as string,
            fields: args.fields as Array<{ key: string; label: string; type: string; required?: boolean; placeholder?: string; defaultValue?: string; options?: string[] }>,
            submitLabel: args.submitLabel as string | undefined,
            _uiAction: "form",
          },
        }
      }

      case "showConfirm": {
        // Render a confirmation dialog inline — user confirms or cancels.
        return {
          success: true,
          data: {
            title: args.title as string,
            message: args.message as string,
            details: args.details as string[] | undefined,
            confirmLabel: args.confirmLabel as string | undefined,
            cancelLabel: args.cancelLabel as string | undefined,
            variant: (args.variant as string) ?? "default",
            _uiAction: "confirm",
          },
        }
      }

      case "showTaskPreview": {
        // Render a task/project preview — user approves or requests edits.
        return {
          success: true,
          data: {
            project: args.project as string,
            tasks: args.tasks as Array<{ title: string; checklist?: string[] }>,
            _uiAction: "taskPreview",
          },
        }
      }

      case "showDatePicker": {
        // Render a date picker with suggestions — user picks a date.
        return {
          success: true,
          data: {
            label: args.label as string,
            mode: (args.mode as string) ?? "date",
            suggestions: args.suggestions as string[] | undefined,
            _uiAction: "datePicker",
          },
        }
      }

      case "undoLastAction": {
        const store = useUndoStore.getState()
        if (!store.canUndo) {
          return { success: false, error: "Nothing to undo" }
        }
        const entry = await store.undo()
        return {
          success: true,
          data: { undone: entry?.description ?? "last action" },
        }
      }

      // -------------------------------------------------------------------
      // Memory tools
      // -------------------------------------------------------------------

      case "getMemoryArea": {
        const area = args.area as string
        const result = await readArea(area)
        if (!result) return { success: false, error: `Area '${area}' not found or memory not initialized` }
        const entries = Object.entries(result.entries).map(([key, e]) => ({
          key,
          label: e.label,
          content: e.content,
          source: e.source,
        }))
        return {
          success: true,
          data: { area, label: result.label, summary: result.summary, entries },
        }
      }

      case "getMemoryDetail": {
        const area = args.area as string
        const key = args.key as string
        const result = await readEntry(area, key)
        if (!result) return { success: false, error: `Memory entry '${key}' not found in area '${area}'` }
        return { success: true, data: { area, key, ...result } }
      }

      case "rememberThis": {
        const text = args.text as string
        const area = args.area as MemoryArea
        const key = args.key as string
        const saved = await saveEntry(area, key, {
          label: key.replace(/_/g, " "),
          content: text,
          source: "explicit",
          confidence: 1.0,
          updatedAt: new Date().toISOString(),
        })
        if (!saved) return { success: false, error: "Failed to save memory — doc not available" }
        return { success: true, data: { saved: true, area, key, content: text } }
      }

      case "updateMemory": {
        const area = args.area as string
        const key = args.key as string
        const newContent = args.newContent as string
        const updated = await updateMemoryEntry(area, key, newContent)
        if (!updated) return { success: false, error: `Failed to update memory '${key}' in area '${area}'` }
        return { success: true, data: { updated: true, area, key, newContent } }
      }

      case "listMemoryAreas": {
        const areas = await listAreas()
        return {
          success: true,
          data: {
            areas: areas.map((a) => ({
              area: a.key,
              label: a.label,
              summary: a.summary || "(empty)",
              entryCount: a.topicCount,
            })),
            totalEntries: areas.reduce((sum, a) => sum + a.topicCount, 0),
          },
        }
      }

      default:
        return { success: false, error: `Unknown tool: ${name}` }
    }
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : String(e),
    }
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function wrapOpResult(result: OpResult<unknown>): ToolExecResult {
  if (result.ok) {
    return { success: true, data: result.data }
  }
  return { success: false, error: result.error }
}

function summarizeTask(task: DbObject) {
  // Extract container key from parentPath (e.g. "root/UUID" → "UUID", "root/#inbox" → "#inbox")
  const segments = task.parentPath?.split("/")
  const containerKey = segments && segments.length >= 2 ? segments[1] : null
  return {
    id: task.id,
    title: task.title,
    status:
      task.status === ObjectStatus.Completed
        ? "completed"
        : task.status === ObjectStatus.Canceled
          ? "canceled"
          : "active",
    dueAt: task.dueAt ?? null,
    tags: task.tags ?? [],
    note: task.note ? task.note.slice(0, 200) : null,
    parentPath: task.parentPath ?? null,
    containerKey,
  }
}
